import { Box, Card, CardContent, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import {
  CarrierVisitDirection,
  ContainerJourneyDto,
  OrderResponseDto,
  OrderStatus,
} from '@planning/app/api'
import { ContainerDirectionChip } from '@planning/components/ContainerDirectionChip'
import { OrderAlerts } from '@planning/components/molecules/OrderAlerts'
import { CustomAlert } from '@planning/pages/TallymanV2/Components'
import { GateClerkSearchResultDto } from '@planning/stores/gateClerk/GateClerkViewStore'
import { canOrderProceed } from '@planning/utils/order-utils'
import { useHoldEnforcement } from '@planning/utils/hold-enforcement'
import { isValidityExpiredByNow } from '@planning/utils/release-validity-utils'
import { useTranslate } from '@tolgee/react'
import { HighlightedText } from '@tom-ui/ui'
import { FC } from 'react'
import { observer } from 'mobx-react-lite'

interface Props {
  containerNumber: string
  highlight?: string
  onClick?: () => void
  children?: React.ReactNode
  direction?: CarrierVisitDirection
  order?: GateClerkSearchResultDto
  releaseEnd?: string | null
  isOnTerminal?: boolean
}

export const GateClerkSearchResult: FC<Props> = observer(
  ({
    containerNumber,
    highlight,
    onClick,
    children,
    direction,
    order,
    releaseEnd,
    isOnTerminal,
  }) => {
    const { tenantStore, gateClerkViewStore } = usePlanningStore()
    const { t } = useTranslate()
    const { reaction } = useHoldEnforcement(order, 'GateIn', direction)
    const isBlockedByHold = reaction === 'Block'
    const isValidState =
      !isBlockedByHold &&
      canOrderProceed({
        allowGateInWhenUnitNotOnTerminal: tenantStore.allowGateInWhenUnitNotOnTerminal,
        direction,
        hasBlockingHold: isBlockedByHold,
        isOnTerminal,
        isReleaseExpired: isValidityExpiredByNow(releaseEnd),
        skipReleaseOrder: tenantStore.skipReleaseOrder,
      })

    const openInboundForADifferentCarrierValidation = (order: ContainerJourneyDto | undefined) => {
      if (!order) return false
      return !!gateClerkViewStore.ordersByContainerNumber.find(
        item =>
          item.containerNumber === order.containerNumber &&
          item.direction === CarrierVisitDirection.Inbound &&
          item.carrierVisitId !== null,
      )
    }

    const openOutboundForADifferentCarrierValidation = (
      order: ContainerJourneyDto | OrderResponseDto | undefined,
    ) => {
      if (!order) return false
      return !!gateClerkViewStore.pickUpOrderSearchStore.orders.find(
        item =>
          item.containerNumber === order.containerNumber &&
          item.direction === CarrierVisitDirection.Outbound &&
          item.carrierVisitId !== null,
      )
    }

    const OpenOrderWithDifferentCarrierAlert = () => (
      <CustomAlert
        message={t(
          'containerHasOpenOrderForADifferentCarrier',
          'Container has an open order for a different carrier',
        )}
        severity='warning'
      />
    )

    return (
      <Card
        sx={{
          mb: '1rem',
        }}
        onClick={() => {
          if (isValidState) onClick?.()
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant='h3'>
              <HighlightedText highlight={highlight}>{containerNumber}</HighlightedText>
            </Typography>
            {direction ? (
              <ContainerDirectionChip
                direction={direction}
                label={direction === 'Inbound' ? t('dropOff', 'Drop Off') : t('pickUp', 'Pick Up')}
              />
            ) : (
              ''
            )}
          </Box>

          {isValidState && children}

          {openInboundForADifferentCarrierValidation(order) && (
            <OpenOrderWithDifferentCarrierAlert />
          )}

          {openOutboundForADifferentCarrierValidation(order) && (
            <OpenOrderWithDifferentCarrierAlert />
          )}

          <OrderAlerts
            status={order?.status ?? OrderStatus.Fulfilled}
            order={order}
            direction={direction}
            isOnTerminal={isOnTerminal}
            releaseEnd={releaseEnd}
            invalidAllocationSpace={order?.invalidAllocationSpace}
            plannedYardLocation={order?.plannedYardLocation}
          />
        </CardContent>
      </Card>
    )
  },
)
